import React from 'react';
import { ImageStyle, StyleProp, ViewStyle } from 'react-native';
import { TextProps } from 'react-native-svg';

import PlayerImagePlaceholder from '@/assets/images/playerImagePlaceholder';
import { Box } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { CollegeBasketballProps, CollegeFootballProps } from '@/components/player-profile/tiles/college-sports/types';
import { PlayerWithTeam } from '@/feature/betslip-pickem/types';
import { CIRCLE_SIZE } from '@/feature/entries-pickem/components/PickCircleWithStatus';
import { PLAYER_IMAGE_SIZE } from '@/feature/lobby/components/PlayerRowSmall';
import { PLAYER_SIZE } from '@/feature/lobby/components/PlayersHeadshotsList';
import { PlayerProfileSheetImageCanvas } from '@/feature/player-profile-sheets/components/PlayerProfileSheetImageCanvas';
import { PlayerProfileSheetPlaceholder } from '@/feature/player-profile-sheets/components/PlayerProfileSheetPlaceholder';
import { CONTAINER_SIZE } from '@/feature/player-profile-sheets/utils/player-profile-utils';
import { designSystem, withOpacity } from '@/styles/styles';
import { League } from '@/types/api.generated';

/*
 *  The type of player needed to render a PlayerAvatar. It is based on `PlayerWithTeam` but:
 *  omits properties that are irrelevant for the avatar display (`position`, `sport`, `projections`, and the full `team` object).
 *  and replaces `team` with a simplified object containing only `color` and `secondaryColor`, since the avatar only needs team colors.
 *  We need this because in some components where we display PlayerAvatar, data like league, jersey number, or team colors
 *  are coming from the 'pick' object and not from the player object
 *  */
export type PlayerAvatarData = Omit<PlayerWithTeam, 'position' | 'sport' | 'projections' | 'team'> & {
    team?: {
        color?: string;
        secondaryColor?: string;
    };
};

export type PlayerAvatarVariant = 'headshot' | 'playerRow' | 'entryPick' | 'profileHeader';

export type PlayerAvatarConfig = {
    shirtSize: { width: number; height: number } | ((player: PlayerAvatarData) => { width: number; height: number });
    svgTextProps: TextProps | ((player: PlayerAvatarData) => TextProps);
    collegeBasketballProps: (player: PlayerAvatarData) => CollegeBasketballProps;
    collegeFootballProps: (player: PlayerAvatarData) => CollegeFootballProps;
    collegeShirtStyleContainer?: StyleProp<ViewStyle> | ((player: PlayerAvatarData) => StyleProp<ViewStyle>);
    collegeSvgNumberContainerStyle?: StyleProp<ViewStyle>;
    imageStyle?: StyleProp<ImageStyle>;
    renderPlaceholder?: () => React.ReactNode;
    renderImage?: (player: PlayerAvatarData, onImageError: () => void) => React.ReactNode;
};

const getProfileHeaderSize = (player: PlayerAvatarData) => {
    const isCBB = player.league === League.Cbb;
    return { width: isCBB ? 57 : 88, height: isCBB ? 99 : 87 };
};

const createPlayerPlaceholder = ({ width, height, color }: { width: number; height: number; color?: string }) => {
    return <PlayerImagePlaceholder width={width} height={height} color={color} />;
};

const COLLEGE_SVG_NUMBER_CONTAINER_STYLE: StyleProp<ViewStyle> = {
    position: 'absolute',
    width: '100%',
    height: '100%',
    top: 16,
};

export const PLAYER_AVATAR_CONFIG: Record<PlayerAvatarVariant, PlayerAvatarConfig> = {
    headshot: {
        shirtSize: { width: PLAYER_SIZE, height: PLAYER_SIZE },
        svgTextProps: {
            fill: ds.colorValue.icon.primary,
            stroke: designSystem.colors.neutral900,
            strokeWidth: '0.5',
            y: 10,
            x: 36 / 2,
            fontSize: '13',
            fontWeight: '600',
            textAnchor: 'middle',
            fontFamily: 'Oswald-SemiBold',
            letterSpacing: -1.2,
        },
        collegeBasketballProps: player => ({
            playerId: player.id,
            teamColor: player.team?.color,
            secondaryColor: player.team?.secondaryColor,
            blur: 10,
            maskWidth: 80,
            maskHeight: 80,
            maskX: -20,
            maskY: -20,
        }),
        collegeFootballProps: player => ({
            playerId: player.id,
            teamColor: player.team?.color,
            secondaryColor: player.team?.secondaryColor,
            blur: 5,
            maskR: 45,
        }),
        collegeShirtStyleContainer: {
            width: 36,
            height: 36,
            alignItems: 'center',
            borderRadius: 17,
            overflow: 'hidden',
            marginTop: 2,
            marginRight: 2,
        },
        collegeSvgNumberContainerStyle: COLLEGE_SVG_NUMBER_CONTAINER_STYLE,
        imageStyle: {
            width: 36,
            height: 36,
            borderRadius: 17,
            marginTop: 2, // 1px border top + 1px border bottom
            marginRight: 2, // 1px border right + 1px border left
        },
        renderPlaceholder: () => {
            return (
                <Box
                    width={36}
                    height={36}
                    borderRadius={'r16'}
                    overflow={'hidden'}
                    alignItems={'center'}
                    marginTop={'s2'}
                    marginRight={'s2'}
                >
                    {createPlayerPlaceholder({
                        width: PLAYER_SIZE,
                        height: PLAYER_SIZE,
                        color: designSystem.colors.neutral300,
                    })}
                </Box>
            );
        },
    },
    playerRow: {
        shirtSize: { width: PLAYER_IMAGE_SIZE, height: PLAYER_IMAGE_SIZE },
        svgTextProps: {
            fill: ds.colorValue.icon.primary,
            stroke: designSystem.colors.neutral900,
            strokeWidth: '0.5',
            y: 25,
            x: 20,
            fontSize: '13',
            fontWeight: '600',
            textAnchor: 'middle',
            fontFamily: 'Oswald-SemiBold',
            letterSpacing: -1.2,
        },
        collegeShirtStyleContainer: player => {
            const marginTop = player.league === League.Cbb ? 0 : 2;
            return { overflow: 'hidden', marginTop: marginTop };
        },
        collegeSvgNumberContainerStyle: { position: 'absolute', width: '100%', height: '100%' },
        imageStyle: { width: PLAYER_IMAGE_SIZE, height: PLAYER_IMAGE_SIZE },
        collegeBasketballProps: player => ({
            playerId: player.id,
            teamColor: player.team?.color,
            secondaryColor: player.team?.secondaryColor,
        }),
        collegeFootballProps: player => ({
            playerId: player.id,
            teamColor: player.team?.color,
            secondaryColor: player.team?.secondaryColor,
        }),
        renderPlaceholder: () => (
            <Box width={PLAYER_IMAGE_SIZE} height={PLAYER_IMAGE_SIZE} marginTop={'s2'}>
                {createPlayerPlaceholder({
                    width: PLAYER_IMAGE_SIZE,
                    height: PLAYER_IMAGE_SIZE,
                    color: withOpacity(ds.colorValue.icon.primary, 0.12),
                })}
            </Box>
        ),
    },
    entryPick: {
        shirtSize: { width: CIRCLE_SIZE, height: CIRCLE_SIZE },
        svgTextProps: {
            fill: ds.colorValue.icon.primary,
            stroke: designSystem.colors.neutral900,
            strokeWidth: '0.5',
            y: 10,
            x: 36 / 2,
            fontSize: '13',
            fontWeight: '600',
            textAnchor: 'middle',
            fontFamily: 'Oswald-SemiBold',
            letterSpacing: -1.2,
        },
        collegeSvgNumberContainerStyle: COLLEGE_SVG_NUMBER_CONTAINER_STYLE,
        collegeShirtStyleContainer: {
            width: 36,
            height: 36,
            alignItems: 'center',
            borderRadius: 18,
            overflow: 'hidden',
            paddingTop: 1,
        },
        imageStyle: {
            width: 36,
            height: 36,
            borderRadius: 38,
        },
        collegeBasketballProps: player => ({
            playerId: player.id,
            teamColor: player.team?.color,
            secondaryColor: player.team?.secondaryColor,
            blur: 10,
            maskWidth: 80,
            maskHeight: 80,
            maskX: -20,
            maskY: -20,
        }),
        collegeFootballProps: player => ({
            playerId: player.id,
            teamColor: player.team?.color,
            secondaryColor: player.team?.secondaryColor,
            blur: 5,
            maskR: 45,
        }),
        renderPlaceholder: () => (
            <Box
                width={36}
                height={36}
                borderRadius={'r16'}
                overflow={'hidden'}
                alignItems={'center'}
                paddingTop={'s4'}
            >
                {createPlayerPlaceholder({ width: CIRCLE_SIZE, height: CIRCLE_SIZE })}
            </Box>
        ),
    },
    profileHeader: {
        shirtSize: getProfileHeaderSize,
        collegeShirtStyleContainer: player => {
            const { width, height } = getProfileHeaderSize(player);
            return {
                alignItems: 'center',
                borderRadius: 18,
                overflow: 'hidden',
                marginTop: 8,
                width: width,
                height: height,
            };
        },
        collegeSvgNumberContainerStyle: COLLEGE_SVG_NUMBER_CONTAINER_STYLE,
        svgTextProps: player => {
            const { width, height } = getProfileHeaderSize(player);
            return {
                fill: ds.colorValue.icon.primary,
                stroke: designSystem.colors.neutral900,
                strokeWidth: '0.72',
                y: height / 2 + 5,
                x: width / 2,
                fontSize: '28.8',
                fontWeight: '600',
                textAnchor: 'middle',
                fontFamily: 'Oswald-SemiBold',
                letterSpacing: -1.2,
            };
        },
        collegeBasketballProps: player => ({
            playerId: player.id,
            teamColor: player.team?.color,
            secondaryColor: player.team?.secondaryColor,
            blur: 10,
            maskWidth: 130,
            maskHeight: 130,
            maskX: -20,
            maskY: -10,
        }),
        collegeFootballProps: player => ({
            playerId: player.id,
            teamColor: player.team?.color,
            secondaryColor: player.team?.secondaryColor,
            blur: 11,
            maskR: 80,
        }),
        renderPlaceholder: () => (
            <PlayerProfileSheetPlaceholder
                width={CONTAINER_SIZE.width}
                height={CONTAINER_SIZE.height}
                background={designSystem.colors.neutral900}
                opacity={0.4}
            />
        ),
        renderImage: (player, onImageError) => (
            <PlayerProfileSheetImageCanvas key={player.id} source={player.icon} onError={onImageError} />
        ),
    },
};
